import {
  Component,
  Input,
  inject,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { formatCount } from '../../../../../utils/sms';

@Component({
  selector: 'app-routing-tab',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './routing-tab.component.html',
  styleUrl: './routing-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutingTabComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tabList: { title: string; count?: number; link: string; queryParams?: Record<string, string> }[] = [];
  @ViewChildren('tabEl') tabEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('outer') outerEl?: ElementRef<HTMLElement>;
  @ViewChild('thumb') thumbEl?: ElementRef<HTMLElement>;

  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Counts read as plain figures with thousands separators: 1,204. */
  readonly formatCount = formatCount;

  activeIndex = 0;

  private subs = new Subscription();
  private resizeObserver?: ResizeObserver;
  /** The tab the thumb last settled on; -1 until it is first placed. */
  private thumbIndex = -1;

  ngOnInit(): void {
    this.syncActiveIndex(this.router.url);
    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          this.syncActiveIndex(e.urlAfterRedirects);
          this.cdr.markForCheck();
          this.schedulePlaceThumb();
        }),
    );
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    // Re-measure whenever a tab changes size: the font arriving, counts
    // loading, or the strip first becoming visible inside a modal.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.placeThumb());
      this.observeTabs();
    }
    this.schedulePlaceThumb();

    this.subs.add(
      this.tabEls.changes.subscribe(() => {
        this.observeTabs();
        this.schedulePlaceThumb();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private syncActiveIndex(url: string): void {
    const [path, search = ''] = url.split('?');
    const urlParams = new URLSearchParams(search);

    let bestIdx = -1;
    let bestLen = 0;

    this.tabList.forEach((tab, i) => {
      if (!path.startsWith(tab.link) || tab.link.length < bestLen) return;

      if (tab.queryParams !== undefined) {
        const tabKeys = Object.keys(tab.queryParams);
        if (tabKeys.length === 0) {
          // Default tab (no queryParams): active only when no sibling tab's params match the URL
          const siblingMatches = this.tabList.some(
            (other) =>
              other.queryParams !== undefined &&
              Object.keys(other.queryParams).length > 0 &&
              Object.entries(other.queryParams).every(
                ([k, v]) => urlParams.get(k) === String(v)
              )
          );
          if (siblingMatches) return;
        } else {
          // Specific param tab: all its queryParams must match the URL
          if (!tabKeys.every((k) => urlParams.get(k) === String(tab.queryParams![k]))) return;
        }
      }

      bestIdx = i;
      bestLen = tab.link.length;
    });

    this.activeIndex = bestIdx >= 0 ? bestIdx : 0;
  }

  private observeTabs(): void {
    const observer = this.resizeObserver;
    if (!observer) return;
    observer.disconnect();
    this.tabEls.forEach((tab) => observer.observe(tab.nativeElement));
  }

  private schedulePlaceThumb(): void {
    if (!this.isBrowser) return;
    setTimeout(() => this.placeThumb());
  }

  /**
   * Sizes the thumb to the active tab and slides it there with a transform.
   * Writes straight to the element so it never waits on change detection.
   */
  private placeThumb(): void {
    const thumb = this.thumbEl?.nativeElement;
    const tab = this.tabEls?.get(this.activeIndex)?.nativeElement;
    if (!thumb || !tab || !tab.offsetWidth) return;

    thumb.style.width = `${tab.offsetWidth}px`;
    thumb.style.transform = `translateX(${tab.offsetLeft}px)`;

    if (this.thumbIndex === this.activeIndex) return;
    const firstPlacement = this.thumbIndex === -1;
    this.thumbIndex = this.activeIndex;
    this.revealTab(tab, !firstPlacement);

    if (firstPlacement) {
      // Commit the first position before transitions switch on, so the
      // thumb appears in place rather than sliding in from the left edge.
      thumb.classList.add('is-placed');
      thumb.getBoundingClientRect();
      thumb.classList.add('is-live');
    }
  }

  /** Scrolls an overflowing strip just far enough to show the active tab. */
  private revealTab(tab: HTMLElement, smooth: boolean): void {
    const outer = this.outerEl?.nativeElement;
    if (!outer || outer.scrollWidth <= outer.clientWidth) return;

    const box = outer.getBoundingClientRect();
    const rect = tab.getBoundingClientRect();
    const gutter = 24;
    let delta = 0;
    if (rect.left < box.left) delta = rect.left - box.left - gutter;
    else if (rect.right > box.right) delta = rect.right - box.right + gutter;
    if (!delta) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    outer.scrollBy({ left: delta, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  }
}
